import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  PlayArrow as PlayArrowIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { googleApi } from "../../../services/api/jtl/google";
import { JTLCredentialsApi } from "../../../services/api/jtl/jtl_credentials";
import { sheetsSettingsApi } from "../../../services/api/jtl/sheets";
import { JtlApi } from "../../../services/api/jtl/jtl";

const getDateMinusDaysDDMMYYYY = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

const CheckingTab = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Date selection states
  const [selectedStartDate, setSelectedStartDate] = useState(
    getDateMinusDaysDDMMYYYY(10),
  );
  const [selectedEndDate, setSelectedEndDate] = useState(
    getDateMinusDaysDDMMYYYY(3),
  );
  const [deliveryTimeAdd, setDeliveryTimeAdd] = useState(3);

  // File states
  const [fileLoading, setFileLoading] = useState(false);
  const [availableFiles, setAvailableFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);

  // Checking states
  const [checkingStatus, setCheckingStatus] = useState("idle");
  const [checkingStatusMessage, setCheckingStatusMessage] = useState("");

  // System status states (will be passed as props or fetched)
  const [googleCredentials, setGoogleCredentials] = useState([]);
  const [sheetsSettings, setSheetsSettings] = useState({});
  const [columnSettings, setColumnSettings] = useState({});
  const [JTLCredentials, setJTLCredentials] = useState([]);

  useEffect(() => {
    loadAvailableFiles();
    loadSystemData();
  }, []);

  const loadSystemData = async () => {
    try {
      setLoading(true);
      const [googleCreds, jtlCreds, sheetSettings] = await Promise.all([
        googleApi.getCredentials(),
        JTLCredentialsApi.getCredentials(),
        sheetsSettingsApi.getSettings(),
      ]);

      setGoogleCredentials(googleCreds.data || []);
      setJTLCredentials(jtlCreds.data || []);

      if (sheetSettings.data) {
        setSheetsSettings({
          spreadsheetId: sheetSettings.data.spreadsheet_id,
        });
        setColumnSettings({
          dateColumn: sheetSettings.data.date_column,
          orderPlaceColumn: sheetSettings.data.order_place_column,
          unitColumn: sheetSettings.data.unit_column,
          asinSkuColumn: sheetSettings.data.asin_sku_column,
          sendStatusColumn: sheetSettings.data.send_status_column,
          confirmationColumn: sheetSettings.data.confirmation_column,
        });
      }
    } catch (error) {
      console.error("Failed to load system data:", error);
      setError(
        error.response?.data?.detail ||
          error.message ||
          "Failed to load system data",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (filename) => {
    setSelectedFiles((prev) =>
      prev.includes(filename)
        ? prev.filter((f) => f !== filename)
        : [...prev, filename],
    );
  };

  const handleDownloadSelected = async () => {
    for (const filename of selectedFiles) {
      try {
        const response = await JtlApi.download_eval_file(filename);
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        setSuccess("Eval file downloaded successfully!");
      } catch (error) {
        console.error("Failed to download eval file:", error);
        setError(
          error.response?.data?.detail ||
            error.message ||
            "Failed to download eval file",
        );
      } finally {
        setSelectedFiles([]);
      }
    }
  };

  const startCheck = async () => {
    try {
      setCheckingStatus("running");
      setCheckingStatusMessage("Initializing check...");
      setError(null);
      const response = await JtlApi.run_check(
        selectedStartDate,
        selectedEndDate,
        deliveryTimeAdd,
      );
      setCheckingStatusMessage(
        response.data.message || "Check started successfully",
      );
      await loadAvailableFiles();
    } catch (error) {
      console.error("Failed to start check:", error);
      setCheckingStatus("error");
      setError(
        error.response?.data?.detail ||
          error.message ||
          "Failed to start check",
      );
    }
  };

  const loadAvailableFiles = async () => {
    try {
      setFileLoading(true);
      setError(null);
      const response = await JtlApi.list_eval_files();
      const files = Array.isArray(response.data.files)
        ? response.data.files
        : [];
      setAvailableFiles(files);
    } catch (error) {
      console.error("Failed to load available files:", error);
      setError(
        error.response?.data?.detail ||
          error.message ||
          "Failed to load available files",
      );
    } finally {
      setFileLoading(false);
    }
  };

  const checkSystemStatus = () => {
    const hasActiveCredential = googleCredentials.some(
      (cred) => cred.active_inbound_checker && cred.status === "authorized",
    );
    const hasValidSheetSettings =
      sheetsSettings.spreadsheetId &&
      (columnSettings.dateColumn ||
        columnSettings.unitColumn ||
        columnSettings.asinSkuColumn ||
        columnSettings.sendStatusColumn ||
        columnSettings.orderPlaceColumn ||
        columnSettings.confirmationColumn);
    const hasActiveJTLCredential = JTLCredentials.some((cred) => cred.active);
    return {
      hasActiveCredential,
      hasValidSheetSettings,
      hasActiveJTLCredential,
      isReady:
        hasActiveCredential && hasValidSheetSettings && hasActiveJTLCredential,
    };
  };

  return (
    <Box sx={{ p: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h5" fontWeight="bold">
            Checking Center
          </Typography>
        </Box>
      </Box>

      {/* System Status and Date Selection */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* System Status Summary */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                System Status
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    {checkSystemStatus().hasActiveCredential ? (
                      <CheckCircleIcon sx={{ color: "success.main" }} />
                    ) : (
                      <ErrorIcon sx={{ color: "error.main" }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="Google Credentials"
                    secondary={
                      checkSystemStatus().hasActiveCredential
                        ? "Active and authorized credential found"
                        : "No active or authorized credentials"
                    }
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    {checkSystemStatus().hasValidSheetSettings ? (
                      <CheckCircleIcon sx={{ color: "success.main" }} />
                    ) : (
                      <ErrorIcon sx={{ color: "error.main" }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="Sheets Settings"
                    secondary={
                      checkSystemStatus().hasValidSheetSettings
                        ? "Valid sheet configuration found"
                        : "Sheet settings incomplete"
                    }
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    {checkSystemStatus().hasActiveJTLCredential ? (
                      <CheckCircleIcon sx={{ color: "success.main" }} />
                    ) : (
                      <ErrorIcon sx={{ color: "error.main" }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="JTL Credentials"
                    secondary={
                      checkSystemStatus().hasActiveJTLCredential
                        ? "Active and authorized credential found"
                        : "No active or authorized credentials"
                    }
                  />
                </ListItem>
              </List>

              {checkSystemStatus().isReady ? (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    ✅ System is ready for checking process!
                  </Typography>
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    ⚠️ Please complete the configuration in other tabs before
                    starting checking process.
                  </Typography>
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Date Selection */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Checking Period
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  fullWidth
                  label="Start Date"
                  placeholder="dd.mm.yyyy"
                  value={selectedStartDate}
                  onChange={(e) => {
                    // Only allow digits and dots, and max length 10
                    let val = e.target.value
                      .replace(/[^0-9.]/g, "")
                      .slice(0, 10);
                    // Auto-insert dots
                    if (val.length > 2 && val[2] !== ".")
                      val = val.slice(0, 2) + "." + val.slice(2);
                    if (val.length > 5 && val[5] !== ".")
                      val = val.slice(0, 5) + "." + val.slice(5);
                    setSelectedStartDate(val);
                  }}
                  inputProps={{ maxLength: 10 }}
                />
                <TextField
                  fullWidth
                  label="End Date"
                  placeholder="dd.mm.yyyy"
                  value={selectedEndDate}
                  onChange={(e) => {
                    let val = e.target.value
                      .replace(/[^0-9.]/g, "")
                      .slice(0, 10);
                    if (val.length > 2 && val[2] !== ".")
                      val = val.slice(0, 2) + "." + val.slice(2);
                    if (val.length > 5 && val[5] !== ".")
                      val = val.slice(0, 5) + "." + val.slice(5);
                    setSelectedEndDate(val);
                  }}
                  inputProps={{ maxLength: 10 }}
                />
                <TextField
                  fullWidth
                  label="Additional Delivery Days"
                  type="number"
                  value={deliveryTimeAdd}
                  onChange={(e) => setDeliveryTimeAdd(e.target.value)}
                />

                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    📅 Selected: {selectedStartDate || "-"} -{" "}
                    {selectedEndDate || "-"} +{" "}
                    <span style={{ color: "red", fontWeight: 600 }}>
                      {deliveryTimeAdd}
                    </span>{" "}
                    Days
                  </Typography>
                </Alert>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* File Download Section */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Download Eval File
              </Typography>

              {availableFiles.length === 0 && !fileLoading ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    No evaluation files available. Create some invoices first,
                    then refresh.
                  </Typography>
                </Alert>
              ) : null}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <FormControl
                  fullWidth
                  disabled={fileLoading || availableFiles.length === 0}
                >
                  <InputLabel>Available Files</InputLabel>
                  <Select
                    multiple
                    value={selectedFiles}
                    label="Available Files"
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {selected.map((filename) => (
                          <Chip
                            key={filename}
                            label={filename}
                            color="success"
                            icon={<CheckCircleIcon />}
                            sx={{
                              bgcolor: "success.light",
                              color: "white",
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {availableFiles.map((filename) => (
                      <MenuItem
                        key={filename}
                        value={filename}
                        onClick={() => handleFileSelect(filename)}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          <Typography
                            sx={{
                              flexGrow: 1,
                              color: selectedFiles.includes(filename)
                                ? "success.main"
                                : "inherit",
                              fontWeight: selectedFiles.includes(filename)
                                ? 700
                                : 400,
                            }}
                          >
                            {filename}
                          </Typography>
                          {selectedFiles.includes(filename) && (
                            <CheckCircleIcon
                              sx={{ color: "success.main", ml: 1 }}
                            />
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadSelected}
                  disabled={selectedFiles.length === 0 || fileLoading}
                  sx={{ mt: 1 }}
                >
                  Download Selected
                </Button>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={loadAvailableFiles}
                  disabled={fileLoading}
                  sx={{ mt: 1 }}
                  startIcon={
                    fileLoading ? <CircularProgress size={16} /> : null
                  }
                >
                  {fileLoading ? "Loading..." : "Refresh Available Files"}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* Checking Control */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Checking Control
          </Typography>

          <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={
                checkingStatus === "running" ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  <PlayArrowIcon />
                )
              }
              onClick={startCheck}
              disabled={
                !checkSystemStatus().isReady && checkingStatus !== "running"
              }
              sx={{ minWidth: 200 }}
            >
              {checkingStatus === "running" ? "Stop Check" : "Start Check"}
            </Button>
            <Chip
              label={
                checkingStatus === "idle"
                  ? "Completed"
                  : checkingStatus.charAt(0).toUpperCase() +
                    checkingStatus.slice(1)
              }
              color={
                checkingStatus === "idle"
                  ? "success"
                  : checkingStatus === "completed"
                    ? "success"
                    : checkingStatus === "error"
                      ? "error"
                      : checkingStatus === "running"
                        ? "primary"
                        : "default"
              }
              variant="outlined"
            />
          </Box>

          {/* Status Message */}
          {checkingStatusMessage && (
            <Alert
              severity={
                checkingStatus === "error"
                  ? "error"
                  : checkingStatus === "completed"
                    ? "success"
                    : "info"
              }
              sx={{ mt: 2 }}
            >
              <Typography variant="body2">{checkingStatusMessage}</Typography>
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CheckingTab;
